import { Card, ClientDetailsHeader, Drawer, Flex } from "components/common"
import { useGetSalesPlayBookClientDetailsQuery } from "features/leads/api"
import { useCallback, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ClientSummary, ProjectKnowledge, SalesPlaybook } from "./components"
import ModifyProjectPopover from "./components/ModifyProjectPopOver"
import styles from "./index.module.less"
import { localisedValue } from "utils"

export const PROJECT_TABS = {
  SALES_PLAYBOOK: "sales-playbook",
  CLIENTS_SUMMARY: "clients-summary",
  PROJECT_KNOWLEDGE: "project-knowledge",
}

export const DRAWER_CONFIG = {
  TITLE: "PropGo Call Center",
  WIDTH: 1200,
  PLACEMENT: "right",
}

const SalesPlaybookDrawer = ({ refs }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [client, setClient] = useState(null)
  const [activeTab, setActiveTab] = useState(PROJECT_TABS.SALES_PLAYBOOK)
  const [showCallBanner, setShowCallBanner] = useState(false)
  const [filterObject, setFilterObject] = useState({})

  useImperativeHandle(refs, () => ({
    openDrawer: ({ client, triggeredBy = null } = {}) => {
      setClient(client)
      triggeredBy === "callButton" && setShowCallBanner(true)
      setOpen(true)
    },
  }))

  const { data: clientDetailsData, isFetching } = useGetSalesPlayBookClientDetailsQuery(client?.id, {
    skip: !open || !client?.id,
  })

  const tabItems = useMemo(
    () => [
      { tab: t("Sales PlayBook"), key: PROJECT_TABS.SALES_PLAYBOOK },
      { tab: t("Client Summary"), key: PROJECT_TABS.CLIENTS_SUMMARY },
      {
        tab: t("Project Knowledge"),
        key: PROJECT_TABS.PROJECT_KNOWLEDGE,
        actionButton: <ModifyProjectPopover filterObject={filterObject} setFilterObject={setFilterObject} />,
      },
    ],
    [t, filterObject]
  )

  const tabContent = useMemo(
    () => ({
      [PROJECT_TABS.CLIENTS_SUMMARY]: (
        <Flex className={styles.tabContent}>
          <ClientSummary clientId={client?.id} clientDetails={clientDetailsData} />
        </Flex>
      ),
      [PROJECT_TABS.SALES_PLAYBOOK]: (
        <Flex className={styles.tabContent}>
          <SalesPlaybook clientId={client?.id} />
        </Flex>
      ),
      [PROJECT_TABS.PROJECT_KNOWLEDGE]: <ProjectKnowledge clientId={client?.id} filterObject={filterObject} />,
    }),
    [client?.id, clientDetailsData, filterObject]
  )

  const handleDrawerClose = useCallback(() => {
    setFilterObject({})
    setActiveTab(PROJECT_TABS.SALES_PLAYBOOK)
    setOpen(false)
  }, [])

  const handleCallBannerClose = useCallback(() => setShowCallBanner(false), [])

  const descriptionItems = useMemo(() => {
    return [
      {
        object: {
          ...clientDetailsData?.city,
          loc: clientDetailsData?.country?.locCode,
          countryName: localisedValue({ key: "name", object: clientDetailsData?.country }),
        },
        key: "name",
        type: "city",
      },
      { value: clientDetailsData?.interest, type: "capitalize" },
      { object: clientDetailsData?.platform, key: "name", type: "readable" },
      { value: clientDetailsData?.source, type: "readable", prefix: t("via") },
    ]
  }, [clientDetailsData, t])

  return (
    <>
      <Drawer
        className={styles.drawer}
        width="65%"
        footer={null}
        destroyOnCLose={true}
        title={
          <ClientDetailsHeader
            id={clientDetailsData?.clientId}
            title={clientDetailsData?.clientName}
            tag={clientDetailsData?.clientStage}
            classification={clientDetailsData?.classification}
            aiScore={clientDetailsData?.aiConversionScore}
            descriptionItems={descriptionItems}
            showCallBanner={showCallBanner}
            onCallBannerClose={handleCallBannerClose}
            email={clientDetailsData?.clientEmail}
            willCallButtonTriggerSalesPlaybook={false}
            client={client}
            loading={isFetching}
          />
        }
        onClick={e => e.stopPropagation()}
        placement={DRAWER_CONFIG.PLACEMENT}
        open={open}
        onClose={handleDrawerClose}
        bodyStyle={{ padding: 0 }}
      >
        <Card
          tabList={tabItems}
          activeTabKey={activeTab}
          onTabChange={setActiveTab}
          headStyle={{ padding: "0px 24px 0 24px" }}
          tabBarExtraContent={tabItems.find(item => item.key === activeTab)?.actionButton}
          bodyStyle={{ padding: 0 }}
          className={styles.drawerCard}
          Shadow={false}
        >
          {tabContent[activeTab]}
        </Card>
      </Drawer>
    </>
  )
}

export default SalesPlaybookDrawer
